'use client';
import React, { useEffect, useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import Breadcrumb from '@/components/molecules/breadcrumb';
import Link from '@/components/atoms/link';
import { sdk } from '@/sdk/CommercetoolsSDK';
import Spinner from '@/components/organisms/spinner';
import { WishlistProps, CustomerWishlistProductItem } from './types';
import { useParams } from 'next/navigation';
import { getLifecycleBadge } from '@/utils/product-lifecycle';

const PAGE_SIZE = 12;
const MAX_PAGE_BUTTONS = 7;

const getLifecycleValue = (lifecycle: any) => {
  if (typeof lifecycle === 'string') return lifecycle;

  return lifecycle?.['en-AU'] ?? lifecycle?.['en-NZ'] ?? lifecycle?.['en-US'] ?? '';
};

const Wishlist = (_props: WishlistProps) => {
  const [items, setItems] = useState<CustomerWishlistProductItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
const [totalResults, setTotalResults] = useState(0);
const { locale } = useParams();
const fetchWishlist = async (page = currentPage) => {
  setLoading(true);
  setError(null);
const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
  try {
    const response = await sdk.composableCommerce.custom.getCustomerWishlist({
      pageNumber: page,
      pageSize: PAGE_SIZE,
      storeKey,
    });

    if (!response.isError && response.data?.data) {
      // console.log('Response data:', response.data.data.items);
      setItems(response.data.data.items ?? []);
      setTotalPages(response.data.data.totalPages ?? 1);
      setTotalResults(response.data.data.totalResults ?? 0);
    } else {
      setError('Failed to load saved products.');
    }

    return response;
  } catch {
    setError('Failed to load saved products.');
  } finally {
    setLoading(false);
  }
};
 useEffect(() => {
  fetchWishlist(currentPage);
}, [currentPage]);

 const handleRemove = async (variantSku: string) => {
  const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';

  try {
    const response =
      await sdk.composableCommerce.custom.removeProductFromWishlist({
        variantSku,
        storeKey,
      });

    if (!response.isError) {
      // If removing the last item on a page other than page 1,
      // move back one page.
      const nextPage =
        items.length === 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;

      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await fetchWishlist(nextPage);
      }
    }
  } catch {
    // silent
  }
};

  const getPageNumbers = () => {
    const half = Math.floor(MAX_PAGE_BUTTONS / 2);
    let start = Math.max(currentPage - half, 1);
    let end = Math.min(start + MAX_PAGE_BUTTONS - 1, totalPages);
    if (end - start + 1 < MAX_PAGE_BUTTONS) {
      start = Math.max(end - MAX_PAGE_BUTTONS + 1, 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const renderLifecycleBadge = (lifecycle: any) => {
    const lifecycleValue = getLifecycleValue(lifecycle);
    const lifecycleBadge = getLifecycleBadge(lifecycleValue);

    if (!lifecycleBadge) return null;

    return (
      <div
        className="absolute left-1 top-1 z-10 flex items-center justify-center rounded-[5px] px-1 py-0.5 text-[6px] font-semibold uppercase text-white"
        style={{ backgroundColor: lifecycleBadge.color }}
      >
        {lifecycleBadge.label}
      </div>
    );
  };

  const pageHeader = (
    <div className="">
      <div className="hidden pb-4 lg:block">
        {/* <Breadcrumb Separator="|">
          <Link href="/" className="capitalize" underlineOnHover={false}>Home</Link>
          <span className="capitalize">Saved Products</span>
        </Breadcrumb> */}
      </div>
      <h2 className="my-5 lg:mb-6 flex w-full flex-col text-3xl md:flex-row md:justify-between">Saved Products</h2>
    </div>
  );

  if (loading && items.length === 0) {
    return (
      <>
        {pageHeader}
        <Spinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        {pageHeader}
        <div className="px-4 py-4 text-red-500">{error}</div>
      </>
    );
  }

  const pageNumbers = getPageNumbers();

  return (
    <>
      {pageHeader}
      <div className=" ">

        {/* ── Mobile / tablet card list ── */}
        <div className="block lg:hidden bg-white">
          {loading && <Spinner />}
          {items.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-base leading-6">No saved products found.</div>
          )}
          {items.map((item) => (
            <div key={item.variantsku} className="border-b border-[#E4E4E7] px-4 py-3">
              {/* Top row: image + info + star */}
              <div className="flex items-start gap-3">
                <div className="relative h-[62px] w-[62px] shrink-0 overflow-hidden rounded border border-[#E4E4E7] bg-white">
                  {renderLifecycleBadge(item.lifecycle)}
                  {item.primaryImage?.src ? (
                    <img src={item.primaryImage.src} alt={item.primaryImage.alt ?? item.name} className="h-full w-full object-contain" />
                  ) : (
                    <div className="h-full w-full bg-neutral-100" />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {item.brand && (
                    <div className="truncate text-sm text-gray-500">
                      <span className="font-medium text-gray-600">Brand:</span> {item.brand}
                    </div>
                  )}
                  <a href={item.url} className="line-clamp-3 text-base font-medium leading-6 hover:underline">
                    {item.name}
                  </a>
                  {item.colour && (
                    <div className="truncate text-sm text-gray-500">
                      <span className="font-medium text-gray-600">Color:</span> {item.colour}
                    </div>
                  )}
                </div>
                <button onClick={() => handleRemove(item.variantsku)} title="Remove from saved products" className="shrink-0 pl-2">
                  <StarIcon className="h-5 w-5 text-gray-800 hover:text-red-500 transition-colors" />
                </button>
              </div>
              {/* Bottom row: code + prices */}
              <div className="mt-2 grid grid-cols-3 gap-1 text-sm text-gray-700">
                <div>
                  <div className="font-medium text-gray-500">Code</div>
                  <div className="font-semibold text-gray-900">{item.variantKey ?? item.variantsku}</div>
                </div>
                <div
  className={
    getLifecycleValue(item.lifecycle)?.toUpperCase()?.trim() === 'PROTOTYPE' ||
    item.builderRange?.toUpperCase()?.trim() === 'YES'
      ? 'invisible'
      : ''
  }
>
                  <div className="font-medium text-gray-500">RRP</div>
                  <div className="font-semibold text-gray-900">
                    {item.price?.displayAmount ?? '-'}
                  </div>
                </div>
                {/* <div>
                  <div className="font-medium text-gray-500">Trade Price</div>
                  <div className="font-semibold text-gray-900">{item.tradePrice?.displayAmount ?? '-'}</div>
                </div> */}
              </div>
            </div>
          ))}
        </div>

        {/* ── Desktop grid ── */}
        <div className="hidden lg:block">
        <div className="relative w-full overflow-x-auto rounded-md border-t border-neutral-400 bg-white">
          {loading && <div className="absolute inset-0 z-10 rounded-md bg-white/60" />}
          {loading && <Spinner />}

          {/* Single grid — header + all rows share the same column widths */}
          <div className="grid grid-cols-[58%_16%_18%_8%]">

            {/* Header cells */}
            <div className="px-3 py-3 text-sm font-bold text-gray-700 bg-[#F4F4F5] border-b-2 border-[#E4E4E7]">Product Name</div>
            <div className="px-3 py-3 text-sm font-bold text-gray-700 whitespace-nowrap bg-[#F4F4F5] border-b-2 border-[#E4E4E7]">Code</div>
            <div className="px-3 py-3 text-sm font-bold text-gray-700 whitespace-normal text-center bg-[#F4F4F5] border-b-2 border-[#E4E4E7]">RRP </div>
            {/* <div className="px-3 py-3 text-sm font-bold text-gray-700 whitespace-normal text-center bg-[#F4F4F5] border-b-2 border-[#E4E4E7]">Trade Price (Inc. GST)</div> */}
            <div className=" py-3 text-sm font-bold text-gray-700 whitespace-normal text-center bg-[#F4F4F5] border-b-2 border-[#E4E4E7]">Saved Products</div>

            {/* Body rows — desktop only */}
            {items.map((item) => {
              const isPriceHidden =
  getLifecycleValue(item.lifecycle)?.toUpperCase()?.trim() === 'PROTOTYPE' ||
  item.builderRange?.toUpperCase()?.trim() === 'YES';
              return (
                <React.Fragment key={item.variantsku}>
                  <div className="px-3 py-2 border-b border-[#E4E4E7]">
                    <div className="flex items-center gap-3">
                      <div className="relative h-[62px] w-[62px] lg:h-[80px] lg:w-[80px] shrink-0 overflow-hidden rounded border border-[#E4E4E7] bg-white">
                        {renderLifecycleBadge(item.lifecycle)}
                        {item.primaryImage?.src ? (
                          <img
                            src={item.primaryImage.src}
                            alt={item.primaryImage.alt ?? item.name}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="h-full w-full bg-neutral-100" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-col gap-0.5">
                        {item.brand && (
                          <div className="truncate text-sm text-gray-500">
                            <span className="font-medium text-gray-600">Brand:</span> {item.brand}
                          </div>
                        )}
                        <a href={item.url} className="line-clamp-3 lg:line-clamp-none text-base leading-6 hover:underline">
                          {item.name}
                        </a>
                        {item.colour && (
                          <div className="truncate text-sm text-gray-500">
                            <span className="font-medium text-gray-600">Color:</span> {item.colour}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 flex items-center text-base leading-6 whitespace-nowrap border-b border-[#E4E4E7]">{item.variantKey ?? item.variantsku}</div>
                  <div className="px-3 py-2 flex items-center justify-center text-base leading-6 whitespace-nowrap border-b border-[#E4E4E7]">
                    <span className={isPriceHidden ? 'invisible' : ''}>
                      {item.price?.displayAmount ?? '-'}
                    </span>
                  </div>
                  {/* <div className="px-3 py-2 flex items-center justify-center text-base leading-6 whitespace-nowrap border-b border-[#E4E4E7]">{item.tradePrice?.displayAmount ?? '-'}</div> */}
                  <div className="px-3 py-2 flex items-center justify-center border-b border-[#E4E4E7]">
                    <button onClick={() => handleRemove(item.variantsku)} title="Remove from saved products">
                      <StarIcon className="h-5 w-5 text-gray-800 hover:text-red-500 transition-colors" />
                    </button>
                  </div>
                </React.Fragment>
              );
            })}

            {items.length === 0 && (
              <div className="col-span-4 px-3 py-4 text-center text-base leading-6">No saved products found.</div>
            )}

          </div>
        </div>
        </div>{/* end hidden lg:block */}

        {/* Pagination */}
        {totalResults > PAGE_SIZE && (
          <div className="mt-5 mb-20 flex cursor-pointer justify-center md:justify-end">
            <nav>
              <ul className="flex">
                <li>
                  <button
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                    className="border border-[#DDDDDD] px-3 py-3 text-14 text-[#808080] disabled:opacity-40"
                  >
                    <ChevronLeftIcon width={20} />
                  </button>
                </li>
                {pageNumbers.map((number) => (
                  <li
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`border border-[#DDDDDD] px-4 py-2 text-[#808080] ${
                      currentPage === number ? '!border-[#2B2C39] bg-[#DDDDDD] !text-[#174F1C]' : ''
                    }`}
                  >
                    <button className="h-full w-full">{number}</button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalPages}
                    className="border border-[#DDDDDD] px-3 py-3 text-14 text-[#808080] disabled:opacity-40"
                  >
                    <ChevronRightIcon width={20} />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

      </div>
    </>
  );
};

export default Wishlist;